import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import * as countryCodesListModule from 'country-codes-list';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const countryCodesList = (countryCodesListModule as any).default ?? countryCodesListModule;

export interface CountryOption {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

export const buildCountryOptions = (): CountryOption[] => {
  const raw = countryCodesList.customList('countryCode', '{countryNameEn}|{flag}|{countryCallingCode}');
  return Object.entries(raw)
    .map(([code, value]) => {
      const [name, flag, dialCode] = (value as string).split('|');
      return { code, name, flag, dialCode };
    })
    .filter(c => c.dialCode && c.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

interface Props {
  value: string;
  onChange: (dialCode: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

export const IndicativoPaisField: React.FC<Props> = ({
  value, onChange, onBlur, error = false, helperText = '', size = 'medium',
}) => {
  const countryOptions = useMemo(buildCountryOptions, []);

  return (
    <Autocomplete
      options={countryOptions}
      value={countryOptions.find(c => c.dialCode === value) ?? null}
      onChange={(_, selected) => onChange(selected?.dialCode ?? '')}
      onBlur={onBlur}
      getOptionLabel={c => `${c.flag} +${c.dialCode}`}
      filterOptions={(opts, { inputValue }) => {
        const q = inputValue.toLowerCase().replaceAll(/^\+/g, '');
        return opts.filter(c =>
          c.name.toLowerCase().includes(q) || c.dialCode.includes(q)
        );
      }}
      renderOption={(props, c) => (
        <Box
          component="li"
          {...props}
          key={`${c.code}-${c.dialCode}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}
        >
          <span>{c.flag}</span>
          <span>+{c.dialCode}</span>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.name}
          </Typography>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          required
          size={size}
          label="País"
          error={error}
          helperText={helperText || ' '}
          sx={{ '& .MuiFormHelperText-root': { mt: 0.75, mx: 0, minHeight: '1.1em', lineHeight: 1.2 } }}
        />
      )}
      isOptionEqualToValue={(opt, val) => opt.dialCode === val.dialCode}
      disableClearable={!!value}
      slotProps={{
        paper: { sx: { minWidth: 320 } },
        listbox: { sx: { maxHeight: 300 } },
      }}
    />
  );
};
